import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { getProfile, getSavedJobs, getApplications, applyToJob, saveJob, unsaveJob } from "../lib/db";

export const AppCtx = createContext({});
export const useApp = () => useContext(AppCtx);

export function AppProvider({ children }) {
  const [page,        setPage]        = useState("home");
  const [user,        setUserState]   = useState(null);
  const [profile,     setProfile]     = useState(null);
  const [session,     setSession]     = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [savedJobs,   setSavedJobs]   = useState(new Set());
  const [appliedJobs, setAppliedJobs] = useState(new Set());
  const [toast,       setToast]       = useState(null);

  // ── Message recipient — shared state so NetworkPage can set it ────────────
  const [messageRecipient, setMessageRecipient] = useState(null);

  // ── Session bootstrap ────────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUserState(session?.user ?? null);
      if (session?.user) loadUserData(session.user.id);
      else setAuthLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUserState(session?.user ?? null);
        if (session?.user) {
          // Check if this is an employer signup (from Google OAuth redirect)
          if (localStorage.getItem("vorsia-employer-signup") === "true") {
            localStorage.removeItem("vorsia-employer-signup");
            await supabase.from("profiles").upsert({
              id:        session.user.id,
              role:      "employer",
              onboarded: true,
              full_name: session.user.user_metadata?.full_name || "",
            });
          }
          await loadUserData(session.user.id);
        } else {
          setProfile(null);
          setSavedJobs(new Set());
          setAppliedJobs(new Set());
          setAuthLoading(false);
        }
      }
    );
    return () => subscription.unsubscribe();
  }, []);

  async function loadUserData(userId) {
    try {
      const [profileRes, savedRes, appliedRes] = await Promise.all([
        getProfile(userId),
        getSavedJobs(userId),
        getApplications(userId),
      ]);
      if (profileRes.data)  setProfile(profileRes.data);
      if (savedRes.data)    setSavedJobs(new Set(savedRes.data.map(r => r.job_id)));
      if (appliedRes.data)  setAppliedJobs(new Set(appliedRes.data.map(r => r.job_id)));
    } catch (err) {
      console.error("loadUserData error:", err);
    } finally {
      setAuthLoading(false);
    }
  }

  // ── Auth ─────────────────────────────────────────────────────────────────
  const signUp = useCallback(async (email, password, metadata = {}) => {
    return supabase.auth.signUp({ email, password, options: { data: metadata } });
  }, []);

  const signIn = useCallback(async (email, password) => {
    return supabase.auth.signInWithPassword({ email, password });
  }, []);

  const signInWithGoogle = useCallback(async () => {
    return supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setPage("home");
    showToast("Signed out. See you soon!");
  }, []);

  // ── Navigation ────────────────────────────────────────────────────────────
  const navigate = useCallback(p => setPage(p), []);

  // ── Navigate to messages with a specific recipient ────────────────────────
  const openMessage = useCallback((recipient) => {
    setMessageRecipient(recipient);
    setPage("messages");
  }, []);

  // ── Jobs ──────────────────────────────────────────────────────────────────
  const toggleSave = useCallback(async (jobId) => {
    const isSaved = savedJobs.has(jobId);
    setSavedJobs(prev => {
      const next = new Set(prev);
      isSaved ? next.delete(jobId) : next.add(jobId);
      return next;
    });
    if (user) {
      isSaved ? await unsaveJob(user.id, jobId) : await saveJob(user.id, jobId);
    }
  }, [savedJobs, user]);

  const applyJob = useCallback(async (jobId, title, company) => {
    if (appliedJobs.has(jobId)) return;
    setAppliedJobs(prev => new Set(prev).add(jobId));
    showToast(`Applied to ${title} at ${company}! 🎉`);
    if (user) await applyToJob(user.id, jobId);
  }, [appliedJobs, user]);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const showToast = useCallback(msg => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }, []);

  // ── setUser shim ──────────────────────────────────────────────────────────
  const setUser = useCallback((updater) => {
    setProfile(prev => typeof updater === "function" ? updater(prev) : { ...(prev || {}), ...updater });
  }, []);

  const displayName =
    profile?.full_name ||
    user?.user_metadata?.full_name ||
    user?.email?.split("@")[0] ||
    "there";

  return (
    <AppCtx.Provider value={{
      user, session, profile, setProfile, authLoading,
      signUp, signIn, signInWithGoogle, signOut,
      page, navigate,
      openMessage, messageRecipient, setMessageRecipient,
      setUser,
      savedJobs, toggleSave, appliedJobs, applyJob,
      toast, showToast,
      displayName,
    }}>
      {children}
    </AppCtx.Provider>
  );
}
